// One square by the keyboard (issue 1175; design/12 §4): the ONE rule for a single token's step, used by the board's
// arrow keys and by the Tokens list, so both send exactly the `object.move` a drag sends and refuse in the same words.
// The screen's refusals come first (yours to move, the turn, a wall this screen holds); the server's are the protection.
// In a fight (qa-runs#1434) a step names its kind of move as a confirmed drag does — the one picked, Move if none, the
// turn's second Move once the first runs out — so the server counts it, spends it and checks it at the campaign's level.
// A Run's rest carries over the next steps as a Move's does (qa-runs#1456); a Charge picked is refused: it is dragged.
import {
  NO_SPEED_NOTE, WALL_IN_THE_WAY, withBlockSpeeds, type BlockSpeeds, isGridless, moveLeftLabel, movePlan, previewRefusal, routeBlocked, routeCost, stepCell, stepTakenNote,
  type ActionRules, type DistanceRules, type EventBody, type Grid, type PlayerEncounter, type Point, type Size, type Span, type Values, type Wall,
} from '@dragons-table/vtt-shared';
import { moveRev } from './group';
import { confirmedMove, fightFor } from './moveArea';

export interface StepThing { id: string; kind: string; name?: string; x: number; y: number; size: Size; locked: boolean; rev?: number; mine?: boolean; values?: Partial<Values> }

/** The keys that step a square (qa-runs#1434): the arrows and WASD, and the diagonals Q, E, Z and C beside them. */
export const STEP_KEYS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
  a: [-1, 0], d: [1, 0], w: [0, -1], s: [0, 1], q: [-1, -1], e: [1, -1], z: [-1, 1], c: [1, 1],
};
/** The number pad's diagonals, by where the key sits (its code, whatever Num Lock makes its `key`). */
const PAD_KEYS: Readonly<Record<string, readonly [number, number]>> = { Numpad7: [-1, -1], Numpad9: [1, -1], Numpad1: [-1, 1], Numpad3: [1, 1] };

/** The square a key steps, or null. A letter with Ctrl, Alt or Cmd is a shortcut (Ctrl+Z, Ctrl+C), never a step. */
export function stepOfKey(e: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey'>): readonly [number, number] | null {
  const pad = PAD_KEYS[e.code];
  if (pad) return pad;
  const s = STEP_KEYS[e.key];
  if (!s) return null;
  return e.key.length === 1 && (e.ctrlKey || e.metaKey || e.altKey) ? null : s;
}

export interface StepWorld {
  grid: Grid;
  extent: { width: number; height: number };
  /** The walls this screen holds: the DM's; a player holds none and is told by the server. */
  walls: readonly Wall[];
  isDm: boolean;
  /** A co-DM with the tokens power (issue 1159) moves anything. */
  movesAny: boolean;
  /** The DM placing the table (issue 811): a step lands with no route, and walls do not hold. */
  placing: boolean;
  /** This viewer's reading of the fight, for the turn's refusal and what the step spends. */
  encounter: Pick<PlayerEncounter, 'running' | 'active' | 'combatants' | 'turn'> | null;
  /** What a step spends (qa-runs#1434): the table's movement, the scene's Free move, the kind picked, the difficult terrain held. */
  moves?: { distance: DistanceRules | null; actions: ActionRules | null | undefined; freeMove: boolean; pick: string | null | undefined; difficult: readonly Span[]; block?: BlockSpeeds | null | undefined } | undefined;
}

/** qa-runs#1456: a charge is one straight line to its foe, so it is dragged; a key step with Charge picked says so. */
export const CHARGE_BY_DRAG = 'A charge goes in one straight line to its target: drag the token to charge, or pick another kind of move to step.';

export const notYours = (name: string | undefined): string => `${name ?? 'That token'} is not yours to move.`;

/** Whether this viewer may move a thing at all (before the turn and the walls). */
export const mayStep = (o: StepThing, w: Pick<StepWorld, 'isDm' | 'movesAny'>): boolean => w.isDm || w.movesAny || o.mine === true;

/**
 * The move one square along, or the screen's refusal; null for a locked thing (it never moves, and the board says nothing).
 * `left`: what the step's kind holds after it ("Move: 20 ft left"), or the note a drop would say; absent when it is free.
 */
export function keyStep(o: StepThing, di: number, dj: number, w: StepWorld): { body: EventBody; to: Point; left?: string } | { say: string } | null {
  if (o.locked) return null;
  if (!mayStep(o, w)) return { say: notYours(o.name) };
  const refusal = w.encounter ? previewRefusal(w.encounter, o.id, w.isDm || w.movesAny ? 'dm' : 'player') : null;
  if (refusal) return { say: refusal };
  const to = stepCell(w.grid, w.extent, { x: o.x, y: o.y }, o.size, di, dj);
  if (to.x === o.x && to.y === o.y) return { say: 'The edge of the board is in the way.' };
  if (w.walls.length && !(w.isDm && w.placing) && routeBlocked({ x: o.x, y: o.y }, [to], w.walls)) return { say: WALL_IN_THE_WAY };
  const base = { kind: 'object.move' as const, id: o.id, route: w.placing ? [to] : [{ x: o.x, y: o.y }, to], rev: moveRev(o) };
  const m = w.moves;
  // Placing, a thing that is no token, or a screen without the rules: a step moves freely, as before.
  if (!m || w.placing || o.kind !== 'token') return { body: base as EventBody, to };
  // The plan the board's area and a drag's Confirm read (moveArea), from this screen's turn as it stands now.
  const fight = fightFor(w.encounter, o.id, m.distance?.movement, m.actions);
  const plan = movePlan({ freeMove: m.freeMove, distance: m.distance, gridless: isGridless(w.grid), values: withBlockSpeeds(o.values ?? {}, m.block, m.distance?.movement), fight, pick: m.pick ?? undefined });
  if (plan.mode === 'nospeed') return { body: base as EventBody, to, left: NO_SPEED_NOTE };
  if (plan.mode === 'stepped') return { body: base as EventBody, to, left: stepTakenNote(plan.kind.label) };
  if ((plan.mode !== 'kind' && plan.mode !== 'rest') || !m.distance) return { body: base as EventBody, to };
  if (plan.mode === 'kind' && plan.kind.charge) return { say: CHARGE_BY_DRAG };
  // What the step costs, as a drag's readout counts it: the diagonals walked this turn carried on (qa-runs#1433).
  const cost = routeCost({ x: o.x, y: o.y }, [{ x: o.x, y: o.y }, to], o.size, w.grid, w.extent, m.distance, m.difficult, fight?.diagonals ?? 0);
  return { body: confirmedMove(plan, base) as EventBody, to, left: moveLeftLabel(plan.kind.label, plan.budget - (cost?.squares ?? 0), m.distance) };
}

// A counted step waits for the table (qa-runs#1434): the next press is read from the turn the last one changed, so a held
// key never sends two steps from the same Move. It waits for the answer and the turn's change, or a moment at most.
const turnMark = (enc: StepWorld['encounter']): string => {
  const t = enc?.turn as { round?: number; combatant?: string; moveLeft?: number; spent?: readonly unknown[]; diagonals?: number } | undefined;
  return t ? `${t.round}|${t.combatant}|${t.moveLeft}|${t.spent?.length}|${t.diagonals ?? 0}` : '';
};
let waiting: { mark: string; answered: boolean; until: number } | null = null;

/** Whether a counted step is still on its way: a press now is dropped (a held key's repeats among them). */
export function stepWaiting(enc: StepWorld['encounter']): boolean {
  if (!waiting) return false;
  if (Date.now() > waiting.until || (waiting.answered && turnMark(enc) !== waiting.mark)) waiting = null;
  return waiting !== null;
}

/** A counted step sent: wait for it. Returns its answer's handler (a refusal ends the wait at once). */
export function stepSent(enc: StepWorld['encounter']): (ok: boolean) => void {
  const mine = { mark: turnMark(enc), answered: false, until: Date.now() + 1500 };
  waiting = mine;
  return (ok) => { if (waiting !== mine) return; if (ok) mine.answered = true; else waiting = null; };
}

/** Where a thing stands, in squares (the shared rule, so the history and the rewind list say it the same way). */
export { squareOf, squareWords } from '@dragons-table/vtt-shared';
